import React from "react";
import { Link } from "react-scroll";
import { Row, Col } from "react-flexbox-grid";
import { useFlag } from "@unleash/proxy-client-react";

import { useAppContext } from "../Layout/context";
import URL_PATTERNS from "../../../urls";
import { Section } from "../../atoms/Section";
import { Container } from "../../atoms/Container";
import Title from "../../atoms/Title";
import ListAlpha from "../../atoms/ListAlpha";
import { SUBSCRIPTION_MONTHLY_BILLING_WAIT_DAYS, UNLEASH_INVITE_MATE_V2 } from "../../../helpers/constants";

import {
  Well,
  WellTitle,
  ListBullet,
  ListSidebarNumbering,
  ContentHolder,
  ContentTitle,
  ListNumbering,
  Bold,
  Underline,
  Large,
  StyledDisplayNoneSmDown,
} from "./style";

interface TermsUkProps {
  showHeaderBarFromChild: boolean;
}

const TermsUk = ({ showHeaderBarFromChild }: TermsUkProps) => {
  const isReferralV2Enabled = useFlag(UNLEASH_INVITE_MATE_V2);
  const { minBottlesInOrder } = useAppContext();

  return (
    <Section>
      <Container>
        <Row>
          <Col lg={3} md={5}>
            <StyledDisplayNoneSmDown showHeaderBarFromChild={showHeaderBarFromChild}>
              <ListSidebarNumbering>
                <li>
                  <Link to="anchor-1" smooth offset={-90} duration={350}>
                    ACCEPTANCE
                  </Link>
                </li>
                <li>
                  <Link to="anchor-2" smooth offset={-90} duration={350}>
                    Account
                  </Link>
                </li>
                <li>
                  <Link to="anchor-3" smooth offset={-90} duration={350}>
                    Orders
                  </Link>
                </li>
                <li>
                  <Link to="anchor-4" smooth offset={-90} duration={350}>
                    Subscription
                  </Link>
                </li>
                <li>
                  <Link to="anchor-5" smooth offset={-90} duration={350}>
                    Price and payments
                  </Link>
                </li>
                <li>
                  <Link to="anchor-6" smooth offset={-90} duration={350}>
                    Promotional discount codes
                  </Link>
                </li>
                <li>
                  <Link to="anchor-7" smooth offset={-90} duration={350}>
                    Referral program
                  </Link>
                </li>
                <li>
                  <Link to="anchor-8" smooth offset={-90} duration={350}>
                    Extras from us to you
                  </Link>
                </li>
                <li>
                  <Link to="anchor-9" smooth offset={-90} duration={350}>
                    Availability and cancellation
                  </Link>
                </li>
                <li>
                  <Link to="anchor-10" smooth offset={-90} duration={350}>
                    Delivery, title and risk
                  </Link>
                </li>
                <li>
                  <Link to="anchor-11" smooth offset={-90} duration={350}>
                    Happiness promise
                  </Link>
                </li>
                <li>
                  <Link to="anchor-12" smooth offset={-90} duration={350}>
                    Intellectual property
                  </Link>
                </li>
                <li>
                  <Link to="anchor-13" smooth offset={-90} duration={350}>
                    User Content
                  </Link>
                </li>
                <li>
                  <Link to="anchor-14" smooth offset={-90} duration={350}>
                    Exclusions
                  </Link>
                </li>
                <li>
                  <Link to="anchor-15" smooth offset={-90} duration={350}>
                    Limitations
                  </Link>
                </li>
                <li>
                  <Link to="anchor-16" smooth offset={-90} duration={350}>
                    Collection Notice
                  </Link>
                </li>
                <li>
                  <Link to="anchor-17" smooth offset={-90} duration={350}>
                    General
                  </Link>
                </li>
                <li>
                  <Link to="anchor-18" smooth offset={-90} duration={350}>
                    Definitions
                  </Link>
                </li>
              </ListSidebarNumbering>
            </StyledDisplayNoneSmDown>
          </Col>

          <Col lg={9} md={7}>
            <Title tag={1} uppercase>
              TERMS AND CONDITIONS
            </Title>

            <ContentHolder>
              <Well>
                <WellTitle>Our Disclosures</WellTitle>
                Our complete terms and conditions are contained below, but some important points for you to
                know before you become a customer are set out below:
                <ListBullet>
                  <li>
                    We provide both a one-off purchase and a subscription service. By placing an order with us
                    in accordance with our subscription service you are agreeing to order products from us on
                    an ongoing basis and you agree that a minimum term applies to your subscription, as set
                    out in your account.
                  </li>
                  <li>
                    The rates at which reward points accrue is determined by us and may change without notice
                    to you.
                  </li>
                  <li>
                    We may change the rewards available on the Site or the amount of points required to redeem
                    a reward.
                  </li>
                  <li>
                    All of the products on the Site are subject to availability. Due to market conditions
                    outside our control, sometimes we will need to substitute certain products in your order.
                  </li>
                  <li>
                    We may cancel, at any time before delivery and for whatever reason, an order that we have
                    previously accepted. Where we do so, we will refund you using the original payment method
                    used for the order.
                  </li>
                  <li>
                    We may need to change the Price from time to time. Any price changes will apply to you no
                    earlier than 30 days following notice to you. If you do not agree with the new Price, you
                    may cancel your subscription in accordance with these terms.
                  </li>
                  <li>
                    Our liability under these terms is limited to, and must not exceed, us resupplying the
                    products to you, and we will not be liable for consequential loss.
                  </li>
                </ListBullet>
                Nothing in these terms limit your rights under the Consumer Rights Act.
              </Well>
              <ContentTitle id="anchor-1">Acceptance</ContentTitle>
              <ListAlpha>
                <li>
                  This website is operated by Good Pair Days Ltd. of 49 Greek Street, London, W1D 4EG (
                  <Bold>we</Bold>, <Bold>our</Bold> or <Bold>us</Bold>
                  ). It is available at: www.goodpairdays.com/uk and may be available through other addresses
                  or channels including our mobile application (collectively the <Bold>Site</Bold>
                  ).
                </li>
                <li>
                  By accessing and/or using the Site you:
                  <ListNumbering>
                    <li>
                      agree that you have reviewed these Terms and Conditions (<Bold>Terms</Bold>) and our
                      Privacy Policy (available on the Site);
                    </li>
                    <li>
                      confirm to us that you are over 18 years of age and you have the legal capacity to enter
                      into a legally binding agreement with us; and
                    </li>
                    <li>agree to use the Site in accordance with the Terms.</li>
                  </ListNumbering>
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-2">Account</ContentTitle>
              <ListAlpha>
                <li>
                  You are required to create an account in order to purchase products from us. You must ensure
                  that any personal information you give to us when creating an account is accurate and
                  up-to-date. All personal information that you give to us will be treated in accordance with
                  our Privacy Policy.
                </li>
                <li>
                  When you create an account, you will be asked to input basic contact information such as
                  your name, date of birth and email address. You will also be asked to choose a password.
                </li>
                <li>
                  It is your responsibility to keep your account details confidential. You are liable for all
                  activity on your account, including any purchases made using your account details.
                </li>
                <li>
                  If you don{"'"}t make an order for 4 years then your account information is deleted for data
                  privacy reasons.
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-3">Orders</ContentTitle>
              <ListAlpha>
                <li>
                  You may order products from us as set out on the Site. Any order placed through the Site is
                  an offer by you to purchase a particular product or products for the price notified
                  (including the delivery and other charges and taxes) at the time you place your order.
                </li>
                <li>
                  You may make an order for:
                  <ListNumbering>
                    <li>
                      a one-off purchase (<Bold>One-Off Purchase</Bold>
                      );
                    </li>
                    <li>
                      a subscription as described on the Site (<Bold>Subscription</Bold>
                      ).
                    </li>
                  </ListNumbering>
                </li>
                <li>
                  We may, at our absolute discretion, accept or reject an order. We will endeavour to notify
                  you of a rejection at the time of the order or within a reasonable time thereafter.
                </li>
                <li>
                  Each One-Off Purchase order that we accept results in a separate binding agreement between
                  you and us for the supply of products in accordance with the Terms.
                </li>
                <li>
                  It is your responsibility to check the order details, including selected products and
                  pricing, before you submit your order through the Site.
                </li>
                <li>
                  When you order and pay on the Site and your payment has been validated, we will provide you
                  with order details, which may include an order number, an order ID, the delivery status and
                  billing addresses and a description of what was ordered.
                </li>
                <li>
                  Under applicable laws, alcohol can only be sold or supplied to persons aged 18 years or
                  older. By placing an order with us, you confirm that you are at least 18 years old. You
                  agree that it is your responsibility to consume alcohol responsibly and in accordance with
                  the law. You acknowledge that fulfilment of any order may be subject to age verification
                  processes, including in person at the point of delivery, and by placing an order you consent
                  to such checks being carried out by us and our partners.
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-4">Subscription</ContentTitle>
              <ListAlpha>
                <li>
                  With any purchase of three or more full size bottles, or other monthly subscription boxes,
                  we may provide the items listed below. Each item holds independent value and can also be
                  purchased separately through our online store. When they are included automatically in a
                  member’s order of three or more full size bottles, or other monthly subscription boxes, we
                  offer an equivalent discount on member’s total box price.
                </li>
                <ListNumbering>
                  <li>
                    Wine and grape information and recipe cards and leaflets - these provide general wine and
                    grape type education and information, along with recipe guides for specific grape and wine
                    types, available for sale separately.
                  </li>
                  <li>
                    Education cards and leaflets - these contain wine related educational content, available
                    for sale separately.
                  </li>
                  <li>
                    Reward items redeemed with points - must be redeemed alongside an order of three full size
                    bottles or more, available for sale separately.
                  </li>
                  <li>
                    Sample items - samples such as food or drink may be included based on membership level and
                    availability, and available for sale separately.
                  </li>
                  <br />
                </ListNumbering>
                <li>
                  Your Subscription will automatically renew each billing cycle, which will initially be
                  monthly. You will be charged for each order on the calendar day corresponding to when you
                  first placed your order for your Subscription (<Bold>Billing Day</Bold>). You can amend the
                  billing cycle and the Billing Day by changing the frequency in your settings on your account
                  page.
                </li>
                <li>
                  The selection of products to be included in each order under your Subscription (
                  <Bold>Product Mix</Bold>) will be notified to you in writing{" "}
                  {SUBSCRIPTION_MONTHLY_BILLING_WAIT_DAYS} days prior to your Billing Day. The Product Mix
                  will either be:
                  <ListNumbering>
                    <li>made by you based on the selection available on the Site;</li>
                    <li>made by us based on your preferences shared with us on the Site.</li>
                  </ListNumbering>
                </li>
                <li>
                  Once you receive the details of a Product Mix, you have until your Billing Day to notify us
                  in writing if you wish to make any changes to the Product Mix.
                </li>
              </ListAlpha>
              <Underline>Varying, upgrading, downgrading, and cancelling your Subscription</Underline>
              <ListAlpha>
                <li>
                  You may change the frequency of your Subscription at any time by updating your account on
                  the Site (for example changing from quarterly to monthly).
                </li>
                <li>
                  You may upgrade or downgrade your Subscription to another tier at any time in the account
                  page/ manage Subscription (or similar) and the upgrade or downgrade will become effective on
                  the next Billing Day. The payment method linked to your account will automatically be
                  charged the subscription fee for your new Subscription tier on your next Billing Day.
                </li>
                <li>
                  Your Subscription automatically continues until cancelled in accordance with the
                  ‘Cancellation’ clause.
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-5">Price and payments</ContentTitle>
              <Underline>One-Off Purchase</Underline>
              <ListAlpha>
                <li>
                  You must pay us the purchase price of each product you order plus any applicable delivery
                  costs based on the delivery options selected by you as set out on the Site (the{" "}
                  <Bold>Price</Bold>) in accordance with this clause.
                </li>
                <li>
                  In the event of a One-Off Purchase, you must pay the Price via credit card or any other
                  payment methods set out on the Site such as Stripe.
                </li>
                <li>
                  Each One-Off Purchase must include a minimum amount of {minBottlesInOrder} full size
                  bottles, unless otherwise set out on the Site (the{" "}
                  <Bold>Minimum One-Off Purchase Amount</Bold>).
                </li>
                <li>
                  For the avoidance of doubt, the Minimum One-Off Purchase Amount does not apply to One-Off
                  Purchases to the extent that:
                  <ListNumbering>
                    <li>
                      the purchase is for a Special Pack (as advertised on the Site as a special pack); or
                    </li>
                    <li>
                      the purchase is made with the use of a pre-paid gift voucher with a minimum value of
                      $60.
                    </li>
                  </ListNumbering>
                </li>
              </ListAlpha>
              <Underline>Subscription</Underline>
              <ListAlpha>
                <li>
                  If you have ordered a Subscription:
                  <ListNumbering>
                    <li>
                      you must pay us the purchase price for each subscription box you order, any applicable
                      delivery costs based on the delivery options selected by you as set out on the Site and
                      any other amount payable to us under these Terms (<Bold>Subscription Fee</Bold>),
                      without set off or delay via credit card or any other payment methods set out on the
                      Site;
                    </li>
                    <li>
                      you must pay us the Subscription Fee in advance of the next billing cycle on your
                      Billing Day; and
                    </li>
                    <li>
                      you are responsible for reviewing the pricing schedule, inclusions and exclusions
                      associated with your Subscription.
                    </li>
                  </ListNumbering>
                </li>
              </ListAlpha>
              <Underline>General</Underline>
              <ListAlpha>
                <li>
                  All amounts are stated in Pounds Sterling and are inclusive of VAT and duty (where
                  applicable). We display delivery costs separately from the purchase price. We reserve the
                  right to amend or withdraw any special offers at any time without notice.
                </li>
                <li>
                  You must not pay, or attempt to pay, the Price or Subscription Fee by fraudulent or unlawful
                  means. If you make a payment by debit card or credit card, you warrant that you are
                  authorised to use the debit card or credit card to make the payment. We reserve the right to
                  contact your card issuer for the purposes of carrying out security checks.
                </li>
                <li>
                  To the extent permitted by law, the Price and/or Subscription Fee are non-refundable and
                  non-cancellable once paid.
                </li>
                <li>
                  If any payment in relation to a Subscription Fee has not been made as set out on the Site or
                  in the Terms, we may (at our absolute discretion) immediately cease supplying the products.
                </li>
                <li>
                  We may modify our Subscription Fee from time-to-time. Any price changes will apply to you no
                  earlier than 30 days following notice to you. The notice may be provided at any time via
                  email, text message or via notification to your account (and you may change your
                  notification preferences in your account page). If you do not agree to the price change, you
                  may cancel your Subscription in accordance with the ‘Cancellation’ clause.
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-6">Promotional discount codes</ContentTitle>
              <p>
                We may from time to time issue promotional discount codes for certain products on the Site. To
                claim the discount, you must enter the promotional discount code at the time of submitting
                your order through the Site. The conditions of use relating to promotional discount codes will
                be specified on the Site at the time they are issued.
              </p>
              <ContentTitle id="anchor-7">Referral program</ContentTitle>
              <ListAlpha>
                <li>
                  Subject to:
                  <ListNumbering>
                    <li>you referring our Site to another user;</li>
                    <li>
                      such other user creating an account on the Site and placing a monthly order on the Site;
                      and
                    </li>
                    <li>the conditions of use relating to referral codes as specified on the Site,</li>
                  </ListNumbering>
                  <br />
                  {isReferralV2Enabled ? (
                    <p>
                      we may issue you a referral discount code on the Site, send you a gift, or offer your
                      Reward Points to use at our Rewards Store, as set out on the Site. The operation and
                      terms of the referral program are at our sole discretion, and may be amended or
                      withdrawn at any time.
                    </p>
                  ) : (
                    <p>
                      we may issue you a referral discount code on the Site or send you a gift as per our
                      current referral program promotion, as set out on the Site. The operation and terms of
                      the referral program are at our sole discretion, and may be amended or withdrawn at any
                      time.
                    </p>
                  )}
                </li>
                {isReferralV2Enabled ? (
                  <>
                    <li>
                      If receiving gifts for referring a friend as part of your referral offer, you are
                      limited to 3 items per order and a maximum of 10 referrals over the lifetime of your
                      membership. Any subsequent referral rewards will remain on your account to be sent with
                      future orders.
                    </li>
                    <li>
                      If receiving points for referring a friend as part of your referral offer, you are
                      limited to a maximum of 5 referrals over the lifetime of your membership.
                    </li>
                  </>
                ) : (
                  <li>
                    Your refer a friend rewards are limited to 3 items per order and a maximum of 10 referrals
                    over the lifetime of your membership. Any subsequent referral rewards will remain on your
                    account to be sent with future orders.
                  </li>
                )}
              </ListAlpha>
              <ContentTitle id="anchor-8">Extras from us to you</ContentTitle>
              <Underline>Good Pair Days Points</Underline>
              <ListAlpha>
                <li>
                  If you make an order for a One-Off Purchase or a Subscription, you are eligible to earn ‘
                  <Bold>Redeemable Points</Bold>’, that you can use to redeem rewards on the Site, and ‘
                  <Bold>Lifetime Points</Bold>’, that give you access to certain benefits as set out in these
                  Terms (together, the <Bold>Points</Bold>).
                </li>
                <li>
                  Each time you complete a task as set out on the Site, you will receive Points which are
                  recorded in your account.
                </li>
                <li>
                  Except as otherwise provided in these Terms, your Lifetime Points will not expire. If your
                  account is cancelled and you reactivate your account at a later date, your Lifetime Points
                  balance may restore when you reactivate your account. If you create a new account instead of
                  reactivating your old account, your Lifetime Points will not carry over to your new account.
                </li>
                <li>
                  Your Redeemable Points will expire at the end of the 18th consecutive month for which you
                  have not made a purchase from us.
                </li>
                <li>
                  You may check your Points balance on your account page. If we provide you with a refund for
                  an order, we may subtract the Points that were earned on that order. We may correct Points
                  which have been awarded by mistake.
                </li>
                <li>
                  We may change or stop our reward Points programme at any time with no liability to you.
                </li>
                <li>Your Points cannot be converted to or exchanged for money and are not transferrable.</li>
              </ListAlpha>
              <Underline>Redemption of Points</Underline>
              <ListAlpha>
                <li>
                  You can redeem your Redeemable Points for rewards at the time of making a One-Off Purchase
                  or a Subscription order and as otherwise set out on the Site. We will display on the Site
                  the rewards you may redeem your Redeemable Points for and how many Redeemable Points are
                  required to obtain a reward. We may change, without notice and at our discretion, the
                  rewards on the Site and/or the Redeemable Points required to redeem a reward. We do not give
                  any warranty or make any representations in relation to the underlying value of any rewards
                  on the Site.
                </li>
                <li>
                  You must:
                  <ListNumbering>
                    <li>
                      have in your account at least the number of Redeemable Points required to redeem the
                      chosen reward at the time you place an order on the Site to exchange the reward for your
                      Redeemable Points; and
                    </li>
                    <li>
                      be making a One-Off Purchase or a Subscription order in order to be able to redeem a
                      chosen reward.
                    </li>
                  </ListNumbering>
                </li>
                <li>
                  You may be required to pay a delivery fee in order to redeem a reward, as set out on the
                  Site.
                </li>
                <li>
                  If you redeem a reward, it may be subject to personal income or other tax assessment. We
                  recommend you obtain tax advice before redeeming a reward.
                </li>
                <li>
                  To the extent permitted by law, once you have placed an order for a reward using your
                  Redeemable Points, those Points are non-refundable.
                </li>
              </ListAlpha>
              <Underline>Level Benefits</Underline>
              <ListAlpha>
                <li>
                  Lifetime Points can be accumulated if you hold a Subscription or if you have completed a
                  One-Off Purchase. In order to accumulate Lifetime Points from a One-Off Purchase you must
                  sign in and create an account.
                </li>
                <li>
                  If you held a Subscription prior to 2 May 2023:
                  <ListNumbering>
                    <li>
                      your Lifetime Points will include all reward points earned prior to 2 May 2023; and
                    </li>
                    <li>
                      we agree to award you with an additional 1,000 Redeemable Points for you to redeem in
                      accordance with these Terms. These Redeemable Points will not accumulate as Lifetime
                      Points.
                    </li>
                  </ListNumbering>
                </li>
                <li>
                  If you held a Subscription or completed a One-Off Purchase after 2 May 2023, your Lifetime
                  Points total will begin accumulating from the date of your first order on the Site.
                </li>
                <li>
                  There are four Lifetime Point tiers with 10 level benefits associated with your Lifetime
                  Points as set out on the Site (<Bold>Level Benefits</Bold>).
                </li>
                <li>
                  The number of Lifetime Points required to attain and retain the Level Benefits as well as
                  the benefits of each Level Benefit are published at{" "}
                  <a href={URL_PATTERNS.REWARDS_HOW_IT_WORKS} target="_blank" rel="noreferrer">
                    https://www.goodpairdays.com{URL_PATTERNS.REWARDS_HOW_IT_WORKS}
                  </a>
                  .
                </li>
              </ListAlpha>
              <Underline>Goodies with your order</Underline>
              <ListAlpha>
                <li>
                  With any purchase of three or more full size bottles, or other monthly subscription boxes,
                  we may provide the items listed below. Each item holds independent value and can also be
                  purchased separately through our online store. When they are included automatically in a
                  member’s order of three or more full size bottles, or other monthly subscription boxes, we
                  offer an equivalent discount on member’s total box price.
                </li>
                <ListNumbering>
                  <li>
                    Wine and grape information and recipe cards and leaflets - these provide general wine and
                    grape type education and information, along with recipe guides for specific grape and wine
                    types, available for sale separately.
                  </li>
                  <li>
                    Education cards and leaflets - these contain wine related educational content, available
                    for sale separately.
                  </li>
                  <li>
                    Reward items redeemed with points - must be redeemed alongside an order of three full size
                    bottles or more, available for sale separately.
                  </li>
                  <li>
                    Sample items - samples such as food or drink may be included based on membership level and
                    availability, and available for sale separately.
                  </li>
                </ListNumbering>
              </ListAlpha>
              <Underline>Accounts features</Underline>
              <ListAlpha>
                <li>
                  On your account page, you may (subject to change from time to time):
                  <ListNumbering>
                    <li>view and collect badges;</li>
                    <li>complete our wine quizzes;</li>
                    <li>create ‘to try’ lists (which are similar to wish lists);</li>
                    <li>
                      view and update your purchase history for a record of the products you have ordered,
                      drunk or currently have in your possession and are ready to be enjoyed;
                    </li>
                    <li>view your past wines, rate them and make top-up reorders;</li>
                    <li>track your Points progress and history; and</li>
                    <li>view your taste profile which is based on your ratings.</li>
                  </ListNumbering>
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-9">Availability and cancellation</ContentTitle>
              <Underline>Availability</Underline>
              <ListAlpha>
                <li>
                  All purchases made through the Site are subject to availability. We do our best to keep
                  products in stock and to keep the Site up to date with the availability of products.
                </li>
                <li>
                  We reserve the right to cancel, at any time before delivery and for whatever reason, an
                  order that we have previously accepted, including where there is a considerable delay in
                  dispatching your order, if for any reason we cannot supply the products you order (for
                  example for an event beyond our reasonable control) or if products ordered were subject to
                  an error on our Site (for example in relation to a description, price or image). We will
                  contact you using the details you provided when you placed your order.
                </li>
              </ListAlpha>
              <Underline>Suspending your Subscription</Underline>
              <ListAlpha>
                <li>
                  You may suspend your subscription for up to 12 months at any time. You must notify us in
                  writing and/or update your account prior to you being charged on the next Billing Day. If
                  you suspend your subscription before your billing day, you won‘t be charged.
                </li>
              </ListAlpha>
              <Underline>Cancellation</Underline>
              <ListAlpha>
                <li>
                  You can cancel your Subscription and terminate these Terms at any time by notifying us in
                  writing and/or updating your account on the Site. You must notify us in writing and/or
                  update your account prior to you being charged on the next Billing Day. If you cancel your
                  Subscription following you being charged on a Billing Day, your cancellation of the
                  Subscription will be effective from the following billing cycle (meaning you will not be
                  charged on the next Billing Day).
                </li>
                <li>
                  We may immediately suspend, terminate, or limit your access to and use of the Site and
                  terminate these Terms, if you breach the Terms (including your obligation to pay the
                  Subscription Fee) and the breach cannot be remedied or is not remedied within 5 business
                  days of us notifying you of the breach.
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-10">Delivery, title and risk</ContentTitle>
              <ListAlpha>
                <li>
                  We will deliver the products to the delivery address you provide when making your order. We
                  deliver across the UK and if we cannot deliver to you, we will notify you as soon as
                  practicable.
                </li>
                <li>Delivery costs are set out on the Site.</li>
                <li>
                  We normally dispatch products:
                  <ListNumbering>
                    <li>
                      for One-Off Purchases, within 24 business hours of receiving an order, unless otherwise
                      noted on the Site;
                    </li>
                    <li>for Subscriptions, within 24 business hours of the Billing Day.</li>
                  </ListNumbering>
                </li>
                <li>
                  Any delivery periods displayed on the Site are estimates only, based on the information
                  provided by the delivery company.
                </li>
                <li>
                  If you need to change the delivery date or delivery address, please notify us immediately in
                  writing. If you change your delivery address after your Billing Day, we will deliver to the
                  original address and the new delivery address will become effective for your next Billing
                  Day.
                </li>
                <li>
                  We may deliver the products using a range of delivery methods. You may need to sign for some
                  deliveries. If neither you nor your authorised representative is at the delivery address to
                  accept delivery, you agree that we may leave the products at your premises. You acknowledge
                  that our liability in respect of the delivery of products to you is limited under clause 15
                  of these Terms.
                </li>
                <li>
                  Title to the products will remain with us until you have paid us the Price, or the
                  Subscription Fee for that billing cycle in full in accordance with the Terms.
                </li>
                <li>
                  Risk in the products will pass to you as soon as they are delivered to the delivery address
                  you provided in your order.
                </li>
                <li>
                  In respect of One-Off Purchases, you may cancel your One-Off Purchase up to and including 14
                  calendar days after the day on which you receive delivery and we will refund the sum paid
                  including delivery costs (up to the amount charged for the least expensive kind of delivery
                  service we offer) within 14 calendar days after the day on which you tell us that you wish
                  to cancel. However, if you open any of the bottles of wine or remove seals or labels or
                  otherwise tamper with the products before returning them to us, we may reduce your refund as
                  appropriate. Please contact us if you would like to return a One-Off Purchase.
                </li>
                <li>
                  In respect of Subscriptions, you acknowledge that you are requesting that we provide the
                  first order immediately, and that delivery of the first order under your Subscription may be
                  made fewer than 14 days from when you start the Subscription. If we have delivered your
                  first order within that 14 day period, you agree that you will not have the right to cancel
                  the Subscription in relation to that first order.
                </li>
                <li>
                  Nothing in these Terms shall affect your statutory rights in relation to faulty products.
                </li>
                <li>
                  Any refunds will be made using the same payment method originally used to make payment.
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-11">Happiness promise</ContentTitle>
              <ListAlpha>
                <li>
                  If, in respect of a Subscription or a One-Off Purchase, you have notified us that you are
                  not 100% satisfied with the taste of that product, we will add a product of an equivalent
                  value to your next order free of charge (subject to stock availability). You must receive a
                  subsequent order to benefit from the happiness promise. You agree that the happiness promise
                  cannot be redeemed in two consecutive Subscription billing cycles or more than twice in a
                  12-month period, unless otherwise agreed by us.
                </li>
                <li>
                  For the avoidance of doubt, clause 11(a) does not apply where your Subscription is
                  cancelled.
                </li>
                <li>We reserve the right to change or stop the happiness promise at any time.</li>
              </ListAlpha>
              <ContentTitle id="anchor-12">Intellectual property</ContentTitle>
              <ListAlpha>
                <li>
                  All intellectual property (including copyright and trademarks) developed, adapted, modified
                  or created by us or our personnel (including in connection with the Terms, any content on
                  the Site, and our branded products) (<Bold>Our Intellectual Property</Bold>) will at all
                  times vest, or remain vested, in us.
                </li>
                <li>
                  In consideration for the payment of the Subscription Fee we grant you a non-exclusive,
                  non-transferrable (except with our written permission) non-sublicensable, personal and
                  revocable licence to access and use Our Intellectual Property solely for your own personal,
                  non-commercial use and in the manner in which it was intended to be used.
                </li>
                <li>
                  You must not, without our prior written consent:
                  <ListNumbering>
                    <li>
                      use Our Intellectual Property for commercial purposes, including, for example, to
                      advertise your own business, for re-sale, or for any other revenue generation activity;
                    </li>
                    <li>copy or use, in whole or in part, any of Our Intellectual Property;</li>
                    <li>
                      reproduce, retransmit, distribute, disseminate, sell, publish, broadcast or circulate
                      any of Our Intellectual Property to any third party;
                    </li>
                    <li>
                      breach any intellectual property rights connected with the Site or the products,
                      including (without limitation) altering or modifying any of Our Intellectual Property;
                      causing any of any of Our Intellectual Property to be framed or embedded in another
                      website; or creating derivative works from any of Our Intellectual Property.
                    </li>
                  </ListNumbering>
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-13">User Content</ContentTitle>
              <ListAlpha>
                <li>
                  You may be permitted to post, create, upload, publicise or otherwise submit content (
                  <Bold>Submit</Bold>), such as reviews, comments, pictures, and videos on your account (
                  <Bold>User Content</Bold>). You must Submit User Content in accordance with the Terms.
                </li>
                <li>
                  We ask you to limit your discussions to topics which are relevant to the Site and the
                  products.
                </li>
                <li>
                  We reserve the right to remove any reviews or comments which we, in our sole discretion,
                  deem to be in breach of these terms including the ‘Exclusions’ clause and deem to be
                  inappropriate including posts that:
                  <ListNumbering>
                    <li>
                      defame, harass, threaten, stalk, menace, track, monitor, hurt, mistreat, offend or
                      otherwise hurt any person;
                    </li>
                    <li>use obscene or foul language;</li>
                    <li>include link(s) to inappropriate, offensive or illegal material on the forum;</li>
                    <li>
                      could be considered intolerant of a person’s race, culture, appearance, gender, sexual
                      preference, religion, or age; and
                    </li>
                    <li>interfere with another user.</li>
                  </ListNumbering>
                </li>
                <li>
                  We are not responsible for the conduct of any user of our Site. You agree and acknowledge
                  that you participate on the Site including by Submitting User Content at your own risk. We
                  may, at any time (at our sole discretion), remove any User Content.
                </li>
                <li>
                  By making available any User Content on or through our Site, you grant to us a worldwide,
                  irrevocable, perpetual, non-exclusive, transferable, royalty-free licence to use the User
                  Content, with the right to use, view, copy, adapt, modify, distribute, license, transfer,
                  communicate, publicly display, publicly perform, transmit, stream, broadcast, access, or
                  otherwise exploit such User Content on, through or by means of our Site.
                </li>
                <li>
                  You agree that you are solely responsible for all User Content that you make available on or
                  through our Site. You represent and warrant that:
                  <ListNumbering>
                    <li>
                      you are either the sole and exclusive owner of all User Content or you have all rights,
                      licences, consents, and releases that are necessary to grant to us the rights in such
                      User Content (as contemplated by these Terms); and
                    </li>
                    <li>
                      neither the User Content nor the posting, uploading, publication, submission or
                      transmission of the User Content or our use of the User Content on, through or by means
                      of our Site will infringe, misappropriate, or violate a third party’s intellectual
                      property rights, or rights of publicity or privacy, or result in the violation of any
                      applicable law or regulation.
                    </li>
                  </ListNumbering>
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-14">Exclusions</ContentTitle>
              <ListAlpha>
                <li>
                  You must not access or use the Site except as permitted by these Terms and you must not do,
                  omit to do, or authorise any act that would or might invalidate or be inconsistent with our
                  intellectual property rights on the Site. Without limiting the foregoing provisions, you
                  must not and must not permit any other person to:
                  <ListNumbering>
                    <li>resell, assign, transfer, distribute or provide others with access to the Site;</li>
                    <li>
                      use the Site to transmit, publish or communicate material that is defamatory, offensive,
                      abusive, indecent, menacing or unwanted;
                    </li>
                    <li>
                      use the Site in any way that damages, interferes with or interrupts the supply of the
                      Site;
                    </li>
                    <li>
                      introduce malicious programs into our hardware and software or our hardware, software
                      and services which are integrated and operate together, including our networks (
                      <Bold>Systems</Bold>), including viruses, worms, trojan horses and e-mail bombs;
                    </li>
                    <li>reveal your account password to others or allow others to use your account;</li>
                    <li>
                      use the Site to carry out security breaches or disruptions of a network. Security
                      breaches include accessing data where you are not the intended recipient or logging into
                      a server or account that you are not expressly authorised to access or corrupting any
                      data (including network sniffing/monitoring, pinged floods, packet spoofing, denial of
                      service and forged routing information for malicious purposes);
                    </li>
                    <li>
                      use any program/script/command, or send messages of any kind, with the intent to
                      interfere with, or disable, any person’s use of the Site or any training resources from
                      the Site;
                    </li>
                    <li>
                      send any unsolicited messages through or to users of the Site in breach of the Spam Act
                      2003 (Cth) or to send any form of harassment via email, or any other form of messaging,
                      whether through language, frequency, or size of messages or use the Site in breach of
                      any person’s privacy (such as by way of identity theft or “phishing”);
                    </li>
                    <li>
                      use the Site to circumvent user authentication or security of any of your networks,
                      accounts, or hosts or those of your customers or suppliers.
                    </li>
                  </ListNumbering>
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-15">Limitations</ContentTitle>
              <ListAlpha>
                <li>
                  Despite anything to the contrary, to the maximum extent permitted by law:
                  <ListNumbering>
                    <li>neither Party will be liable for any Consequential Loss; and</li>
                    <li>
                      our maximum aggregate liability arising from or in connection with the Terms (including
                      the products and/or the subject matter of the Terms) will be limited to, and must not
                      exceed us resupplying the products to you.
                    </li>
                  </ListNumbering>
                </li>
                <li>
                  Despite anything to the contrary, to the maximum extent permitted by law, we will have no
                  liability, and you release and discharge us from all liability, arising from or in
                  connection with any:
                  <ListNumbering>
                    <li>loss of, or damage to, the products, or any injury or loss to any person;</li>
                    <li>failure or delay in providing the products;</li>
                    <li>breach of the Terms or any law, where caused or contributed to by any:</li>
                    <li>event or circumstance beyond our reasonable control; and</li>
                    <li>
                      act or omission of you or your related parties, and, in any event, any defect, error,
                      omission or lack of functionality or suitability (or the absence of, or reduction in,
                      any anticipated result, outcome or benefit) with respect to the products.
                    </li>
                  </ListNumbering>
                </li>
                <li>
                  Any and all other warranties or conditions not set out in these Terms or provided by
                  applicable law are expressly excluded where permitted.
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-16">Collection Notice</ContentTitle>
              <ListAlpha>
                <li>
                  We collect personal information about you in order to enable you to access and use the Site,
                  to customise your Subscription based on your preferences, to contact and communicate with
                  you, to respond to your enquiries and for other purposes set out in our Privacy Policy.
                </li>
                <li>
                  We may disclose that information to third party service providers who help us deliver our
                  services (including information technology service providers, data storage, web-hosting and
                  server providers, professional advisors, payment systems operators and our business
                  partners) or as required by law. If you do not provide this information, we may not be able
                  to provide our products to you. In certain circumstances, we may disclose your personal
                  information to third parties located, or who store data, outside the United Kingdom.
                </li>
                <li>
                  Our Privacy Policy contains further information about: (i) how we store and use your
                  personal information; (ii) how you can access and seek correction of your personal
                  information; (iii) how you can make a privacy-related complaint; and (iv) our complaint
                  handling process.
                </li>
                <li>
                  By providing personal information to us, you consent to us collecting, holding, using and
                  disclosing your personal information in accordance with our Privacy Policy.
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-17">General</ContentTitle>
              <ListAlpha>
                <li>
                  <Bold>Disputes:</Bold> Neither Party may commence court proceedings relating to any dispute
                  arising from, or in connection with, these Terms without first meeting with a senior
                  representative of the other Party to seek (in good faith) to resolve that dispute, failing
                  which the Parties agree to engage a mediator to attempt to resolve the dispute. The costs of
                  the mediation will be shared equally between the Parties. Nothing in this clause will
                  operate to prevent a Party from seeking urgent injunctive or equitable relief from a court
                  of appropriate jurisdiction.
                </li>
                <li>
                  <Bold>Notices:</Bold> Any notice given under these Terms must be in writing addressed to us
                  at the details set out below or to you at the details provided when setting up or updating
                  your account. Any notice may be sent by standard post or email, and will be deemed to have
                  been served on the expiry of 48 hours in the case of post, or at the time of transmission in
                  the case of transmission by email.
                </li>
                <li>
                  <Bold>Waiver:</Bold> Any failure or delay by a party in exercising a power or right (either
                  wholly or partly) in relation to these Terms does not operate as a waiver or prevent a party
                  from exercising that power or right or any other power or right. A waiver must be in
                  writing.
                </li>
                <li>
                  <Bold>Relationship of parties:</Bold> These Terms are not intended to create a partnership,
                  joint venture or agency relationship between the parties.
                </li>
                <li>
                  <Bold>Photographs:</Bold> If you provide us with photographs of the products via email, on
                  your account page or by tagging us on social media networks, you consent to us publishing
                  such photographs and details (including but not limited to your name, location and date) for
                  our marketing purposes including on the Site and social media.
                </li>
                <li>
                  <Bold>Severance:</Bold> If a provision of these Terms is held to be void, invalid, illegal
                  or unenforceable, that provision is to be read down as narrowly as necessary to allow it to
                  be valid or enforceable, failing which, that provision (or that part of that provision) will
                  be severed from these Terms without affecting the validity or enforceability of the
                  remainder of that provision or the other provisions.
                </li>
                <li>
                  <Bold>Assignment:</Bold> You must not assign any rights or obligations under these Terms,
                  whether in whole or in part, without our prior written consent.
                </li>
                <li>
                  <Bold>Amendment:</Bold> We may, at any time and at our discretion, vary these Terms by
                  providing you with 30 days’ written notice. If you have a Subscription and you do not agree
                  with the variation, you may cancel your Subscription and terminate these Terms in accordance
                  with the ‘Cancellation’ clause. For One-Off Purchases, prior to placing an order, we
                  recommend you carefully read the terms that are in effect at that time to ensure you
                  understand and agree to them. For any One-Off Purchase order that has been accepted by us,
                  the terms and conditions that apply will be the ones that were in effect (and which you
                  agreed to) when you placed your order.
                </li>
                <li>
                  <Bold>Governing law:</Bold> These Terms are governed by the laws of England. Each Party
                  irrevocably and unconditionally submits to the exclusive jurisdiction of the courts
                  operating in England. We make no representation that the Site complies with the laws
                  (including intellectual property laws) of any country outside of the United Kingdom. If you
                  access the Site from outside the United Kingdom, you do so at your own risk and are
                  responsible for complying with these laws in the place you access the Site.
                </li>
                <li>
                  <Bold>FORCE MAJUERE:</Bold> Neither Party will be liable for any delay or failure to perform
                  their respective obligations under these Terms if such delay or failure is caused or
                  contributed to by a Force Majeure Event, provided the Party seeking to rely on the benefit
                  of this clause, as soon as reasonably practical, notified the other Party in writing about
                  the Force Majeure Event and the extent to which it is unable to perform its obligations and
                  uses reasonable endeavours to minimise the duration and adverse consequences of the Force
                  Majeure Event.
                </li>
                <li>
                  <Bold>THIRD PARTY SITES:</Bold> The Site may contain links to websites operated by third
                  parties. Unless we tell you otherwise, we do not control, endorse or approve, and are not
                  responsible for, the content on those websites. We recommend that you make your own
                  investigations with respect to the suitability of those websites. If you purchase goods or
                  services from a third party website linked from the Site, such third party provides the
                  goods and services to you, not us.
                </li>
              </ListAlpha>
              <ContentTitle id="anchor-18">Definitions</ContentTitle>
              <ListAlpha>
                <li>
                  <Bold>Consequential Loss</Bold> means, whether under statute, contract, equity, tort
                  (including negligence), indemnity or otherwise; any loss or damage that cannot be considered
                  to arise according to the usual course of things from the relevant breach, act or omission,
                  whether or not such loss or damage may reasonably be supposed to have been in the
                  contemplation of the Parties at the time they entered into these Terms as the probable
                  results of the relevant breach, act or omission, and/or, any real or anticipated loss of
                  profit, loss of benefit, loss of revenue, loss of business, loss of goodwill, loss of
                  opportunity, loss of savings, loss of reputation, loss of use and/or loss of or corruption
                  of data. The Parties agree that your obligation to pay us the Price under these Terms will
                  not constitute “Consequential Loss”.
                </li>
                <li>
                  <Bold>Force Majeure Event</Bold> means any event or circumstance which is beyond a Party’s
                  reasonable control.
                </li>
              </ListAlpha>
              <Large>
                <Bold>For any questions and notices, please contact us at:</Bold>
                <br />
                Good Pair Days Ltd. of 49 Greek Street, London, W1D 4EG
                <br />
                Email: uk@goodpairdays.com
                <br />
                <Bold>Last update:</Bold> 26 April 2023
              </Large>
            </ContentHolder>
          </Col>
        </Row>
      </Container>
    </Section>
  );
};

export default TermsUk;
